import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const OpsLoginAttempts = () => {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
      start_date: toDateInputValue(start),
      end_date: toDateInputValue(end),
      email: '',
      status: '',
      method: '',
      portal: '',
      ip: '',
      org: '',
    };
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState('');
  const [showPruneConfirm, setShowPruneConfirm] = useState(false);

  const loadAttempts = async (nextFilters = filters, nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/support/login-attempts', {
        params: { ...nextFilters, page: nextPage, page_size: nextPageSize },
      });
      setAttempts(response.data?.attempts || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load login attempts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttempts(filters, page, pageSize);
  }, []);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApply = (event) => {
    event.preventDefault();
    setMessage('');
    setPage(1);
    loadAttempts(filters, 1, pageSize);
  };

  const handleReset = () => {
    setMessage('');
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    const next = {
      start_date: toDateInputValue(start),
      end_date: toDateInputValue(end),
      email: '',
      status: '',
      method: '',
      portal: '',
      ip: '',
      org: '',
    };
    setFilters(next);
    setPage(1);
    loadAttempts(next, 1, pageSize);
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const handlePageChange = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
    loadAttempts(filters, clamped, pageSize);
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/acct-mgmt/v1/support/login-attempts', {
        params: { ...filters, page, page_size: pageSize, export: 'csv' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'login-attempts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export login attempts.');
    }
  };

  const handlePrune = async () => {
    setShowPruneConfirm(false);
    setError('');
    setMessage('');
    try {
      const response = await api.post('/acct-mgmt/v1/support/prune', { target: 'login_attempts' });
      setMessage(`${response.data?.cleared || 0} record(s) pruned.`);
      loadAttempts(filters, page, pageSize);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to prune login attempts.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Login Attempts</h2>
          <div className="text-muted">Review recent sign-in activity across all portals.</div>
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={handleExport}>
          <i className="fas fa-file-csv me-2" aria-hidden="true"></i>
          Export CSV
        </button>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {message && <div className="alert alert-success" role="alert">{message}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Filters</h5>
          <form className="row g-3 align-items-end" onSubmit={handleApply}>
            <div className="col-12 col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={handleChange('start_date')}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={handleChange('end_date')}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Email</label>
              <input
                type="text"
                className="form-control"
                value={filters.email}
                onChange={handleChange('email')}
                placeholder="user@domain.com"
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={handleChange('status')}
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Method</label>
              <input
                type="text"
                className="form-control"
                value={filters.method}
                onChange={handleChange('method')}
                placeholder="password, otp, google, oidc"
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Portal</label>
              <select
                className="form-select"
                value={filters.portal}
                onChange={handleChange('portal')}
              >
                <option value="">All</option>
                <option value="account">Account</option>
                <option value="ops">Ops</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">IP Address</label>
              <input
                type="text"
                className="form-control"
                value={filters.ip}
                onChange={handleChange('ip')}
                placeholder="203.0.113.4"
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Organization</label>
              <input
                type="text"
                className="form-control"
                value={filters.org}
                onChange={handleChange('org')}
                placeholder="Org name or ID"
              />
            </div>
            <div className="col-12 d-flex flex-wrap justify-content-between gap-3">
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Apply Filters
                </button>
                <button type="button" className="btn btn-outline-primary" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
              <div></div>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="card-title d-flex flex-wrap align-items-center justify-content-between w-100 mb-3">
            <h5 className="mb-0">Login Attempts</h5>
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowPruneConfirm(true)}>
              Prune Now
            </button>
          </div>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : attempts.length === 0 ? (
            <div className="text-muted">No login attempts found for the selected range.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Portal</th>
                    <th>IP</th>
                    <th>Organization</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((attempt) => (
                    <tr key={attempt.id}>
                      <td>{new Date(attempt.attempted_at).toLocaleString()}</td>
                      <td>{attempt.email || '-'}</td>
                      <td>{attempt.success ? 'Success' : 'Failed'}</td>
                      <td>{attempt.method || '-'}</td>
                      <td>{attempt.portal || '-'}</td>
                      <td>{attempt.ip_address || '-'}</td>
                      <td>{attempt.org_name || '-'}</td>
                      <td>{attempt.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && total > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
              <div className="text-muted small">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                <span className="small">Page {page} of {totalPages}</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={pageSize}
                  onChange={(e) => {
                    const nextSize = parseInt(e.target.value, 10);
                    setPageSize(nextSize);
                    setPage(1);
                    loadAttempts(filters, 1, nextSize);
                  }}
                >
                  {[50, 100, 200].map((size) => (
                    <option key={size} value={size}>{size}/page</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      {showPruneConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Prune login attempts?</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowPruneConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will remove login attempts older than the retention period configured for this environment.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPruneConfirm(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handlePrune}>
                    Prune Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OpsLoginAttempts;
